import { audioKeyFor, putCachedAudio } from '../audioCache';
import { decode, playBuffer } from '../playBuffer';
import {
  ProviderUnavailable,
  type SpeakOpts,
  type SpeakResult,
  type TtsContext,
  type TtsProvider,
} from '../TtsProvider';
import { MAX_TEXT_CHARS, fetchTtsAudio } from './ttsFetch';

/**
 * Azure AI Speech through our own `/api/tts`, which keeps the subscription key server-side.
 *
 * **Buffered fetch → `decodeAudioData` → Web Audio.** Not `<audio>` streaming, and this inverts
 * what the design originally assumed. Phase 0 measured:
 *
 *     `<audio>` streamed GET    p50 1012 ms   p95 2600 ms
 *     buffered fetch + decode   p50  413 ms   p95  478 ms
 *
 * Safari does not usefully stream the MP3 response, so the `<audio>` path pays the full download
 * plus element overhead and misses NFR4 by 3.7×. The buffered path also happens to be the one that
 * survives the user-gesture restriction, so a single decision serves both the latency budget and
 * dwell/scan operability in Phase 3.
 *
 * The request itself, including its one retry, lives in `ttsFetch.ts` so the retry rule is testable
 * without an audio device.
 */

export interface AzureTtsOptions {
  getVoiceId: () => string;
  isCloudEnabled: () => boolean;
  endpoint?: string;
}

export function createAzureTtsProvider(options: AzureTtsOptions): TtsProvider {
  const endpoint = options.endpoint ?? '/api/tts';

  return {
    id: 'azureTts',

    isAvailable(ctx: TtsContext): boolean {
      // The offline-only switch in Betreuer-Modus is a hard veto, not a preference.
      return ctx.online && ctx.audioUnlocked && options.isCloudEnabled();
    },

    async speak(text: string, opts: SpeakOpts): Promise<SpeakResult> {
      const startedAtMs = performance.now();
      const voiceId = opts.voiceId ?? options.getVoiceId();

      if (text.length > MAX_TEXT_CHARS) {
        throw new ProviderUnavailable('azureTts', `text too long (${text.length})`);
      }

      const bytes = await fetchTtsAudio({
        text,
        voiceId,
        signal: opts.signal,
        endpoint,
        onRetry: (reason) => console.warn('[azure-tts] retrying once after', reason),
      });

      // Decode from a copy: `decodeAudioData` detaches the buffer it is handed, and the cache write
      // below still needs the bytes.
      const buffer = await decode(bytes.slice(0));

      if (opts.isPhrase) {
        // Fire and forget — a cache write must never delay speech.
        void audioKeyFor(text, voiceId).then((key) => putCachedAudio(key, bytes));
      }

      const playback = await playBuffer(buffer, opts.signal);

      return {
        providerId: 'azureTts',
        startedAtMs,
        cached: false,
        ...(playback.silent ? { silent: true } : {}),
        ...(playback.startedPlayingAtMs === undefined
          ? {}
          : { audioAtMs: playback.startedPlayingAtMs }),
      };
    },

    async prerender(text: string, voiceId: string): Promise<Blob> {
      const controller = new AbortController();
      const bytes = await fetchTtsAudio({ text, voiceId, signal: controller.signal, endpoint });
      await putCachedAudio(await audioKeyFor(text, voiceId), bytes);
      return new Blob([bytes], { type: 'audio/mpeg' });
    },
  };
}
